import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { movieCategories, siteInfo } from '@/mocks/movies';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.REACT_APP_NAVIGATE(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  }, [searchQuery]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.3)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-2xl md:text-3xl font-heading font-bold text-primary-500 tracking-wider">
              97<span className="text-foreground-50">影院</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? 'text-primary-500 bg-primary-500/10'
                  : 'text-foreground-200 hover:text-foreground-50 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            {movieCategories.slice(0, 5).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'text-primary-500 bg-primary-500/10'
                    : 'text-foreground-200 hover:text-foreground-50 hover:bg-background-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? 'text-primary-500 bg-primary-500/10'
                  : 'text-foreground-200 hover:text-foreground-50 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-200 hover:text-foreground-50 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-200 hover:text-foreground-50 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50/95 backdrop-blur-md">
          <div className="w-full px-4 md:px-6 py-3">
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索电影..."
                  className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-50 placeholder-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        </div>
      )}

      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50/95 backdrop-blur-md">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="px-3 py-2.5 text-sm font-medium rounded-lg text-foreground-200 hover:text-foreground-50 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              首页
            </Link>
            {movieCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-3 py-2.5 text-sm font-medium rounded-lg text-foreground-200 hover:text-foreground-50 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="px-3 py-2.5 text-sm font-medium rounded-lg text-foreground-200 hover:text-foreground-50 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}